import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Maximize2, Minimize2, Monitor, RotateCw, Smartphone, X } from 'lucide-react';
import { FRAME, HOST, PREVIEW_PATH, previewPages, previewView } from '../../utils/ticketingPreview';

const DEVICES = { desktop: { width: 1280, icon: Monitor, label: 'Desktop' }, mobile: { width: 390, icon: Smartphone, label: 'Mobile' } };
const origin = window.location.origin;

/** Watches an element's size so the preview can scale a full-width page down to fit. */
const useSize = (ref) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setSize({ width: entry.contentRect.width, height: entry.contentRect.height }));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
};

/**
 * Live preview of the premium pages for the unsaved draft. Renders the real public pages in a same-origin iframe
 * (so breakpoints match the device), docked beside the editor on wide screens and full screen when expanded.
 */
const TicketingLivePreview = ({ event, draft, expanded, onExpandedChange, dirty }) => {
  const frameRef = useRef(null);
  const stageRef = useRef(null);
  const stage = useSize(stageRef);
  const [ready, setReady] = useState(false);
  const [path, setPath] = useState(null);
  const [device, setDevice] = useState('desktop');
  const [frameKey, setFrameKey] = useState(0);
  const view = useMemo(() => (event ? previewView(event, draft) : null), [event, draft]);
  const pages = useMemo(() => (view ? previewPages(view) : []), [view]);

  const post = (message) => frameRef.current?.contentWindow?.postMessage({ source: HOST, ...message }, origin);
  const latest = useRef({ view: null, path: null });
  latest.current = { view, path };

  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== origin || e.source !== frameRef.current?.contentWindow || e.data?.source !== FRAME) return;
      if (e.data.type === 'ready') {
        // The frame remounts when the panel is hidden and shown again; give it the draft and the last page right away.
        const { view: current, path: last } = latest.current;
        if (current) post({ type: 'view', view: current, path: last });
        setReady(true);
      }
      if (e.data.type === 'route') setPath(e.data.path);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!ready || !view) return undefined;
    const timer = setTimeout(() => post({ type: 'view', view }), 200);
    return () => clearTimeout(timer);
  }, [ready, view]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onExpandedChange(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded, onExpandedChange]);

  const reload = () => { setReady(false); setPath(null); setFrameKey((k) => k + 1); };
  const width = DEVICES[device].width;
  const scale = stage.width ? Math.min(1, (stage.width - (device === 'mobile' ? 32 : 0)) / width) : 1;
  const current = pages.some((p) => p.path === path) ? path : '';

  return (
    <aside
      aria-label="Live preview"
      className={expanded
        ? 'fixed inset-0 z-50 flex flex-col bg-gray-900/80 p-3 sm:p-6'
        : `sticky top-4 hidden flex-col xl:flex ${dirty ? 'h-[calc(100vh-6.5rem)]' : 'h-[calc(100vh-2rem)]'}`}
    >
      <div className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border bg-white shadow-sm ${expanded ? 'mx-auto w-full max-w-[1400px]' : ''}`}>
        <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
          <span className="mr-1 flex items-center gap-1.5 text-sm font-semibold text-gray-800">
            <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />Live preview
          </span>
          <select
            aria-label="Preview page"
            value={current}
            onChange={(e) => post({ type: 'navigate', path: e.target.value })}
            className="h-8 min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 text-sm"
          >
            {!current && <option value="">{path ? 'Current page' : 'Loading…'}</option>}
            {pages.map((p) => <option key={p.path} value={p.path}>{p.label}</option>)}
          </select>
          <div className="flex rounded-md border border-gray-300 p-0.5" role="group" aria-label="Device">
            {Object.entries(DEVICES).map(([key, { icon: Icon, label }]) => (
              <button key={key} type="button" title={label} aria-label={label} aria-pressed={device === key} onClick={() => setDevice(key)}
                className={`rounded p-1.5 ${device === key ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800'}`}>
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
          <button type="button" onClick={reload} title="Reload preview" aria-label="Reload preview" className="rounded p-1.5 text-gray-500 hover:text-gray-800">
            <RotateCw className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => onExpandedChange(!expanded)} title={expanded ? 'Exit full screen' : 'Full screen'}
            aria-label={expanded ? 'Exit full screen' : 'Full screen'} className="rounded p-1.5 text-gray-500 hover:text-gray-800">
            {expanded ? <Minimize2 className="hidden h-4 w-4 xl:block" /> : <Maximize2 className="h-4 w-4" />}
            {expanded && <X className="h-4 w-4 xl:hidden" />}
          </button>
        </div>
        <p className="border-b bg-blue-50 px-3 py-1.5 text-xs text-blue-800">
          Shows unsaved changes. Uploads, email codes, coupons and payments are simulated; nothing is booked.
        </p>
        <div ref={stageRef} className="relative min-h-0 flex-1 overflow-hidden bg-gray-100">
          {stage.width > 0 && (
            <div
              className={`absolute top-0 ${device === 'mobile' ? 'left-1/2 mt-3 overflow-hidden rounded-[28px] border-[6px] border-gray-800 shadow-lg' : 'left-0'}`}
              style={{
                width: width * scale + (device === 'mobile' ? 12 : 0),
                height: stage.height - (device === 'mobile' ? 24 : 0),
                transform: device === 'mobile' ? 'translateX(-50%)' : undefined,
              }}
            >
              <iframe
                key={frameKey}
                ref={frameRef}
                title="Premium registration preview"
                src={PREVIEW_PATH}
                style={{
                  width,
                  height: (stage.height - (device === 'mobile' ? 36 : 0)) / scale,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  border: 0,
                  background: 'white',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default TicketingLivePreview;
